import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import Multiselect from "multiselect-react-dropdown";
import OptionsOutsideSelect from "./OptionsOutsideSelect";

export default function ProfileDetailsTab({
  setActiveTab,
  data,
  setData,
  projCategory,
  skills,
  setSkills,
  languages,
  spacialLanguages,
  skillValue,
  setSkillValue,
  startDateSelected,
  setStartDateSelected,
  deadlineSelected,
  setDeadlineSelected,
  screeningQuestion,
  setScreeningQuestion,
  options,
  setOptions,
}) {
  const [selectedValues, setSelectedValues] = useState([]);
  const [startDateClassName, setStartDateClassName] = useState("");
  const [deadlineClassName, setDeadlineClassName] = useState("");
  const [allLanguages, setAllLanguages] = useState([]);
  const [showInvalidDate, setShowInvalidDate] = useState(false);
  const [showInvalidDeadlineDate, setShowInvalidDeadlineDate] = useState(false);
  const [DoesNotExist, setDoesNotExist] = useState(false);
  const [suggestedSkills, setSuggestedSkills] = useState([
    "Generalist Consultant",
    "Project Management",
    "Agile Methods",
    "Process Optimization",
  ]);
  const [seniority, setSeniority] = useState([
    { name: "Junior (less than 5 years)", id: 1 },
    { name: "Mid-level (5-14 years)", id: 2 },
    { name: "Senior (15+ years)", id: 3 },
  ]);
  const [multipleChoiceOptions, setMulipleChoiceOptions] = useState([
    { mIndex: 0, options: [] },
  ]);
  const [showOptionsPopup, setShowOptionsPopup] = useState(false);
  const [multipleChoiceIndex, setMultipleChoiceIndex] = useState(0);
  const [showMatrixPopup, setShowMatrixPopup] = useState(false);
  const [matrixIndex, setMatrixIndex] = useState(0);
  const [choiceQuestion, setChoiceQuestion] = useState("");
  const [choiceFocus, setChoiceFocus] = useState(false);
  const [questionsMatrix, setQuestionsMatrix] = useState([{ question: "" }]);
  const [answersMatrix, setAnswersMatrix] = useState([{ answer: "" }]);
  const [languageOptionsOpen, setLanguageOptionsOpen] = useState(null); // Track which language pill has options open
  const [languageRequirementOptions] = useState([
    "Mandatory",
    "Optional",
  ]);
  const languageOptionsRef = useRef(null);

  // Close language options dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageOptionsRef.current &&
        !languageOptionsRef.current.contains(event.target)
      ) {
        setLanguageOptionsOpen(null);
      }
    };

    if (languageOptionsOpen !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [languageOptionsOpen]);

  useEffect(() => {
    if (data.projectConsultantType) {
      if (
        data.projectConsultantType === "int" ||
        data.projectConsultantType === "con"
      ) {
        setData({ ...data, public: true });
      } else {
        setData({ ...data, public: false });
      }
    }
  }, [data.projectConsultantType]);

  useEffect(() => {
    setAllLanguages([...spacialLanguages, ...languages]);
  }, [languages, spacialLanguages]);

  const handleQuestionChange = useCallback(
    (index, value) => {
      setScreeningQuestion((prevQuestions) => {
        const newQuestions = prevQuestions.map((q, i) =>
          i === index ? { ...q, question: value } : q
        );
        setData({ ...data, screeningQuestions: newQuestions });
        return newQuestions;
      });
    },
    [data, setData]
  );
  const handleQuestionUpdate = useCallback(
    (index, value) => {
      setScreeningQuestion((prevQuestions) => {
        const newQuestions = prevQuestions.map((q, i) =>
          i === index ? { ...q, question: value } : q
        );
        setData({ ...data, screeningQuestions: newQuestions });
        return newQuestions;
      });
    },
    [data, setData]
  );
  useEffect(() => {
    if (data.projectConsultantType) {
      if (
        data.projectConsultantType === "int" ||
        data.projectConsultantType === "con"
      ) {
        setData({ ...data, public: true });
      } else {
        setData({ ...data, public: false });
      }
    }
  }, [data.projectConsultantType]);

  const handleOptionsChange = (index, value) => {
    const newOptions = options.map((i) => ({
      value: i.value,
      focus: false,
    }));
    newOptions[index].value = value;
    newOptions[index].focus = true;
    setOptions(newOptions);
  };

  const handleAddOptions = () => {
    const newOptions = [...options];
    newOptions.push("");
    setOptions(newOptions);
  };

  const handleOpenOptions = (index, answerType) => {
    const newQuestions = screeningQuestion.map((i) => ({
      questionNumber: i.questionNumber,
      question: i.question,
      answerType: i.answerType,
      focus: false,
      options: i.options,
      choices: i.choices,
      error: i.error,
    }));
    newQuestions[index].focus = false;
    setScreeningQuestion(newQuestions);
    setData({ ...data, screeningQuestions: newQuestions });
    if (answerType === "Multiple Choice") {
      setShowOptionsPopup(true);
      setMultipleChoiceIndex(index);
      const newOptions = [...multipleChoiceOptions];
      newOptions[0].mIndex = index;
      setMulipleChoiceOptions(newOptions);
      if (newQuestions[index].options.length > 0) {
        setOptions(newQuestions[index].options);
      } else {
        const firstOption = [...options];
        firstOption.push({ value: "", focus: false });
        setOptions(firstOption);
      }
    } else if (answerType === "Matrix") {
      setShowMatrixPopup(true);
      setMatrixIndex(index);
      if (newQuestions[index].options.length > 0) {
        setQuestionsMatrix(newQuestions[index].options);
        setAnswersMatrix(newQuestions[index].choices);
      }
    }
  };

  const handleAnswerTypeChange = (index, value) => {
    const newQuestions = screeningQuestion.map((i, j) => ({
      questionNumber: i.questionNumber,
      question: i.question,
      answerType: j === index ? value : i.answerType,
      focus: j === index ? false : i.focus,
      options: i.options,
      choices: i.choices,
      error: i.error,
    }));
    setScreeningQuestion(newQuestions);
    setData({ ...data, screeningQuestions: newQuestions });
    if (value === "Multiple Choice") {
      setOptions([]);
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = [
      ...screeningQuestion,
      {
        questionNumber: screeningQuestion.length + 1,
        question: "",
        answerType: "Open-Ended",
        focus: false,
        options: [],
      },
    ];
    setScreeningQuestion(newQuestion);
  };

  const handleAddQuestionMatrix = () => {
    setQuestionsMatrix([...questionsMatrix, { question: "" }]);
  };

  const handleRemoveQuestionMatrix = (index) => {
    const newQuestions = questionsMatrix.filter((_, i) => i !== index);
    setQuestionsMatrix(newQuestions);
  };

  const handleRemoveAnswerMatrix = (index) => {
    const newAnswers = answersMatrix.filter((_, i) => i !== index);
    setAnswersMatrix(newAnswers);
  };

  const handleAddAnswerMatrix = () => {
    setAnswersMatrix([...answersMatrix, { answer: "" }]);
  };

  const handleQuestionMatrixChange = (index, value) => {
    const newQuestions = [...questionsMatrix];
    newQuestions[index].question = value;
    setQuestionsMatrix(newQuestions);
  };

  const handleAnswerMatrixChange = (index, value) => {
    const newAnswers = [...answersMatrix];
    newAnswers[index].answer = value;
    setAnswersMatrix(newAnswers);
  };

  const handleCloseModal = () => {
    setShowMatrixPopup(false);
  };

  const handleFinalizeMatrix = () => {
    const fullOptions = [...questionsMatrix];
    const fullChoices = [...answersMatrix];
    // const joinedValuesChoices = fullChoices.map(item => item.answer).join(';');
    // const joinedValuesOptions = fullOptions.map(item => item.question).join(';');
    const newQuestions = [
      ...screeningQuestion.map((i) => ({
        questionNumber: i.questionNumber,
        question: i.question,
        answerType: i.answerType,
        focus: i.focus,
        options: i.options,
        choices: i.choices,
        error: i.error,
      })),
    ];
    if (fullOptions.length === 1 && fullOptions[0].question === "") {
      newQuestions[matrixIndex].error = true;
    } else {
      newQuestions[matrixIndex].error = false;
    }
    if (fullChoices.length === 1 && fullChoices[0].answer === "") {
      newQuestions[matrixIndex].error = true;
    } else {
      newQuestions[matrixIndex].error = false;
    }
    newQuestions[matrixIndex].options = fullOptions;
    newQuestions[matrixIndex].choices = fullChoices;
    setScreeningQuestion(newQuestions);
    setData({ ...data, screeningQuestions: newQuestions });
    setShowMatrixPopup(false);
    setQuestionsMatrix([{ question: "" }]);
    setAnswersMatrix([{ answer: "" }]);
  };

  const addOptions = () => {
    const newOptions = [...options];
    const newQuestions = [
      ...screeningQuestion.map((i) => ({
        questionNumber: i.questionNumber,
        question: i.question,
        answerType: i.answerType,
        focus: i.focus,
        options: i.options,
        choices: i.choices,
        error: i.error,
      })),
    ];
    if (newOptions.length === 1 && newOptions[0].value === "") {
      newQuestions[multipleChoiceIndex].error = true;
    } else {
      newQuestions[multipleChoiceIndex].error = false;
    }
    newQuestions[multipleChoiceIndex].options = newOptions;
    setScreeningQuestion(newQuestions);
    setData({ ...data, screeningQuestions: newQuestions });
    setShowOptionsPopup(false);
    setOptions([]);
  };

  const QuestionRow = React.memo(function QuestionRow({
    index,
    question,
    answerType,
    handleAnswerTypeChange,
    handleAddQuestion,
    handleOpenOptions,
  }) {
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(question);

    useEffect(() => {
      setLocalValue(question);
    }, [question]);

    const onChange = (e) => {
      setLocalValue(e.target.value);
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight}px`;
    };

    const onBlur = () => {
      handleQuestionUpdate(index, localValue);
    };

    return (
      <div
        className="flex-center position-relative mb-1"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          className={`questionRow_${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            flex: 1,
          }}
        >
          <label
            className="fieldlabels"
            style={{ marginBottom: "20px", flexShrink: 0 }}
          >
            Screening Question {index + 1}
          </label>

          <select
            style={{ width: "210px", marginRight: "10px", flexShrink: 0 }}
            className={`questionRow_type_${index}`}
            onChange={(e) => {
              if (e.target.value) {
                handleAnswerTypeChange(index, e.target.value);
              }
            }}
            value={answerType}
          >
            <option value="">Select question type</option>
            <option value="Open-Ended">Open-Ended</option>
            <option value="Yes/No">Yes/No</option>
            <option value="Confidence Level">Confidence Level</option>
            <option value="Multiple Choice">Multiple Choice</option>
            <option value="Matrix">Matrix</option>
          </select>

          <div style={{ position: "relative", flex: 1 }}>
            <textarea
              className={`question${index}`}
              placeholder="Enter question"
              style={{
                width: "100%",
                height: "auto",
                minHeight: "35px",
                paddingRight: "40px",
                boxSizing: "border-box",
                resize: "none",
                overflow: "hidden",
              }}
              ref={inputRef}
              value={localValue}
              onChange={onChange}
              onBlur={onBlur}
            />
            {(answerType === "Multiple Choice" || answerType === "Matrix") && (
              <i
                className="fa fa-pencil addOptions"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "10px",
                  fontSize: "20px",
                  color: "grey",
                  cursor: "pointer",
                }}
                onClick={() => handleOpenOptions(index, answerType)}
              ></i>
            )}
            <div
              className={`questionInvalidTextSign${index}`}
              style={{
                position: "absolute",
                top: "calc(100% + 5px)",
                left: "0",
                color: "red",
                fontWeight: "bold",
                fontSize: "12px",
                display: "none",
              }}
            >
              Options/Choices missing
            </div>
          </div>

          {index === 0 && (
            <i
              className="fa-solid fa-circle-plus"
              style={{
                marginBottom: "20px",
                marginLeft: "10px",
                fontSize: "20px",
                color: "grey",
                cursor: "pointer",
              }}
              onClick={handleAddQuestion}
            ></i>
          )}
        </div>
      </div>
    );
  });

  useEffect(() => {
    setAllLanguages([...spacialLanguages, ...languages]);
  }, [languages, spacialLanguages]);

  const checkboxHandler = (e) => {
    if (e.target.checked) {
      setData({
        ...data,
        projectSuggestedSkills: [...data.projectSuggestedSkills, e.target.id],
      });
    } else {
      setData({
        ...data,
        projectSuggestedSkills: data.projectSuggestedSkills.filter(
          (skill) => skill !== e.target.id
        ),
      });
    }
  };

  const handleSelectChange = (values) => {
    setSkillValue(values);
    setData({ ...data, projectSkillsRequirements: values });
  };

  const handleLanguageRequirementChange = (languageIndex, requirement) => {
    if (!data.projectLanguages || !data.projectLanguages[languageIndex]) return;
    const updatedLanguages = [...data.projectLanguages];
    updatedLanguages[languageIndex] = {
      ...updatedLanguages[languageIndex],
      requirement,
    };
    console.log(updatedLanguages);
    setData({ ...data, projectLanguages: updatedLanguages });
    setLanguageOptionsOpen(null);
  };

  const toggleLanguageOptions = (languageIndex) => {
    setLanguageOptionsOpen(languageOptionsOpen === languageIndex ? null : languageIndex);
  };

  const handleRemoveLanguage = (languageIndex, e) => {
    e.stopPropagation(); // Prevent opening the dropdown when clicking remove
    if (!data.projectLanguages || !data.projectLanguages[languageIndex]) return;
    const updatedLanguages = data.projectLanguages.filter((_, index) => index !== languageIndex);
    setData({ ...data, projectLanguages: updatedLanguages });
    // Close dropdown if the removed language had it open
    if (languageOptionsOpen === languageIndex) {
      setLanguageOptionsOpen(null);
    }
  };

  return (
    <fieldset>
      <div className="form-card">
        <div className="flex-center mb-1">
          <label className="fieldlabels">Seniority</label>
          <div className="w-100">
            <Multiselect
              options={seniority}
              displayValue="name"
              placeholder="Select seniority"
              selectedValues={data.projectSeniority}
              closeIcon="cancel"
              onSelect={(selectedList, selectedItem) => {
                setData({ ...data, projectSeniority: selectedList });
                console.log(data.projectSeniority);
              }}
              onRemove={(selectedList, removedItem) => {
                setData({ ...data, projectSeniority: selectedList });
                console.log(data.projectSeniority);
              }}
              style={{
                chips: {
                  background: "#365561",
                },
                searchBox: {
                  border: "none",
                  width: "100%",
                },
                multiselectContainer: {
                  width: "100%",
                },
                optionContainer: {
                  marginTop: "-21px",
                },
              }}
            />
          </div>
        </div>
        <div className="flex-center mb-1">
          <label className="fieldlabels" style={{ paddingTop: "3px" }}>
            Suggested Skills
          </label>
          <div className="w-100">
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {data.projectCategory ? (
                <>
                  {suggestedSkills.map((skill) => (
                    <div className="prjsetup-skill-checkbox">
                      <input
                        type="checkbox"
                        id={skill}
                        onChange={checkboxHandler}
                      />
                      <label htmlFor={skill}>{skill}</label>
                    </div>
                  ))}
                  {projCategory &&
                    Object.keys(projCategory).map((category) => {
                      if (category === data.projectCategory) {
                        return projCategory[category]["skills"].map((skill) => (
                          <div className="prjsetup-skill-checkbox">
                            <input
                              type="checkbox"
                              id={skill}
                              onChange={checkboxHandler}
                            />
                            <label htmlFor={skill}>{skill}</label>
                          </div>
                        ));
                      } else return null;
                    })}
                </>
              ) : (
                <>
                  {suggestedSkills.map((skill) => (
                    <div className="prjsetup-skill-checkbox">
                      <input
                        type="checkbox"
                        id={skill}
                        onChange={checkboxHandler}
                      />
                      <label htmlFor={skill}>{skill}</label>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex-center mb-1">
          <label className="fieldlabels">Other Skills</label>
          <div className="w-100">
            <OptionsOutsideSelect
              isMulti
              onChange={handleSelectChange}
              options={skills}
              value={skillValue}
            />
          </div>
        </div>
        {screeningQuestion.map((row, index) => (
          <QuestionRow
            key={index}
            index={index}
            question={row.question}
            answerType={row.answerType}
            handleQuestionChange={handleQuestionChange}
            handleAnswerTypeChange={handleAnswerTypeChange}
            handleAddQuestion={handleAddQuestion}
            handleOpenOptions={handleOpenOptions}
          />
        ))}
        <div className="flex-center mb-1">
          <label className="fieldlabels">Language</label>
          <div className="w-100" style={{ position: "relative" }}>
            {/* Custom Language Pills with Nested Options - Above the dropdown */}
            {data.projectLanguages && data.projectLanguages.length > 0 && (
              <div
                ref={languageOptionsRef}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "8px",
                  padding: "6px 8px",
                  minHeight: "36px",
                }}
              >
                {data.projectLanguages.map((language, index) => {
                  // Create a unique key for React
                  const languageKey = `${language.id || index}_${language.name || index}`;
                  return (
                    <div
                      key={languageKey}
                      style={{
                        position: "relative",
                        display: "inline-block",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          backgroundColor: "#365561",
                          color: "white",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "14px",
                          cursor: "pointer",
                          gap: "8px",
                        }}
                        onClick={() => toggleLanguageOptions(index)}
                      >
                        <span>{language.name}</span>
                        {language.requirement && (
                          <span
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.3)",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "12px",
                            }}
                          >
                            {language.requirement}
                          </span>
                        )}
                        <i
                          className="fa fa-chevron-down"
                          style={{
                            fontSize: "10px",
                            marginLeft: "4px",
                          }}
                        />
                        <i
                          className="fa fa-times"
                          onClick={(e) => handleRemoveLanguage(index, e)}
                          style={{
                            fontSize: "12px",
                            marginLeft: "6px",
                            cursor: "pointer",
                            padding: "2px 4px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "rgba(255, 255, 255, 0.4)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                          }}
                        />
                      </div>
                      {languageOptionsOpen === index && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: "0",
                          marginTop: "5px",
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          minWidth: "180px",
                          padding: "8px 0",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            padding: "8px 16px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "#666",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          Select Requirement
                        </div>
                        {languageRequirementOptions.map((option) => (
                          <div
                            key={option}
                            onClick={() =>
                              handleLanguageRequirementChange(index, option)
                            }
                            style={{
                              padding: "10px 16px",
                              cursor: "pointer",
                              fontSize: "14px",
                              backgroundColor:
                                language.requirement === option
                                  ? "#f0f0f0"
                                  : "transparent",
                              color:
                                language.requirement === option
                                  ? "#365561"
                                  : "#333",
                            }}
                            onMouseEnter={(e) => {
                              if (language.requirement !== option) {
                                e.target.style.backgroundColor = "#f5f5f5";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (language.requirement !== option) {
                                e.target.style.backgroundColor = "transparent";
                              }
                            }}
                          >
                            {option}
                            {language.requirement === option && (
                              <i
                                className="fa fa-check"
                                style={{
                                  float: "right",
                                  color: "#365561",
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            )}
            <Multiselect
              options={allLanguages}
              displayValue="name"
              selectedValues={data.projectLanguages}
              placeholder="Select language"
              closeIcon="cancel"
              groupBy={"type"}
              onSelect={(selectedList, selectedItem) => {
                // Initialize requirement for newly selected language - default to "Mandatory"
                const updatedList = selectedList.map((lang) => {
                  if (lang.id === selectedItem.id && !lang.requirement) {
                    return { ...lang, requirement: "Mandatory" };
                  }
                  return lang;
                });
                setData({ ...data, projectLanguages: updatedList });
                console.log(data.projectLanguages);
              }}
              onRemove={(selectedList, removedItem) => {
                setData({ ...data, projectLanguages: selectedList });
                // Close dropdown when any language is removed since indices will shift
                setLanguageOptionsOpen(null);
                console.log(data.projectLanguages);
              }}
              style={{
                chips: {
                  display: "none", // Hide default chips
                },
                searchBox: {
                  border: "none",
                  width: "100%",
                },
                multiselectContainer: {
                  width: "100%",
                },
                optionContainer: {
                  marginTop: "-21px",
                },
              }}
            />
          </div>
        </div>

        <div className="flex-center mb-1 position-relative">
          <label className="fieldlabels">Project Start Date*</label>
          <select
            className={`form-control ${startDateClassName}`}
            style={{ marginTop: "2px" }}
            onChange={(e) => {
              setData({
                ...data,
                projectStartDate: e.target.value,
                projectStartDateOption: e.target.value,
              });
              if (e.target.value && e.target.value !== "Specific start date") {
                setStartDateSelected(true);
                setStartDateClassName("");
                setShowInvalidDate(false);
              } else {
                setStartDateSelected(false);
                setStartDateClassName("");
              }
            }}
            value={data.projectStartDateOption}
          >
            <option value="">Select start date</option>
            <option value="In the next 2 weeks">In the next 2 weeks</option>
            <option value="In the next month">In the next month</option>
            <option value="In the next 3 months">In the next 3 months</option>
            <option value="Specific start date">
              Choose specific start date
            </option>
          </select>
          {data.projectStartDateOption === "Specific start date" && (
            <input
              className={`form-control ml-3 ${startDateClassName}`}
              type="date"
              onChange={(e) => {
                setData({ ...data, projectStartDate: e.target.value });
                if (e.target.value) {
                  setStartDateSelected(true);
                  setStartDateClassName("");
                  setShowInvalidDate(false);
                } else {
                  setStartDateSelected(false);
                  setStartDateClassName("");
                  setShowInvalidDate(true);
                }
              }}
              value={
                data.projectStartDate !== "Specific start date"
                  ? new Date(data.projectStartDate).toISOString().slice(0, 10)
                  : ""
              }
            />
          )}
          {showInvalidDate && (
            <span className="invalid-text-sign">Start date required</span>
          )}
        </div>

        <div className="flex-center mb-1 position-relative">
          <label className="fieldlabels">Deadline*</label>
          <div className="w-100">
            <input
              className={`${deadlineClassName}`}
              type="date"
              onChange={(e) => {
                setData({ ...data, projectDeadline: e.target.value });
                if (e.target.value) {
                  setDeadlineSelected(true);
                  setDeadlineClassName("");
                  setShowInvalidDeadlineDate(false);
                } else {
                  setDeadlineSelected(false);
                  setDeadlineClassName("");
                  setShowInvalidDeadlineDate(true);
                }
              }}
              value={
                data.projectDeadline
                  ? new Date(data.projectDeadline).toISOString().slice(0, 10)
                  : ""
              }
            />
            {showInvalidDeadlineDate && (
              <span className="invalid-text-sign-deadline">
                Project Deadline required
              </span>
            )}
          </div>
        </div>
        <div className="flex-center mb-1">
          <label className="fieldlabels">Project Duration</label>
          <select
            className="form-control"
            onChange={(e) =>
              setData({ ...data, projectDuration: e.target.value })
            }
            value={data.projectDuration}
          >
            <option value="">Select project duration</option>
            <option value="Expert Call">Expert Call</option>
            <option value="Less than one month">Less than one month</option>
            <option value="1-3 months">1-3 months</option>
            <option value="4-6 months">4-6 months</option>
            <option value="More than 6 months">More than 6 months</option>
          </select>
        </div>
        <div className="flex-center mb-1">
          <label className="fieldlabels">Type of Employment</label>
          <select
            className="form-control"
            onChange={(e) =>
              setData({ ...data, projectTypeOfEmployment: e.target.value })
            }
            value={data.projectTypeOfEmployment}
          >
            <option value="">Select type of employment</option>
            <option value="Onsite">Onsite</option>
            <option value="Remote">Remote</option>
            <option value="Hybrid">Hybrid</option>
          </select>
          {data.projectTypeOfEmployment === "Hybrid" && (
            <>
              <input
                type="text"
                placeholder="Remote %"
                className="ml-3"
                onChange={(e) =>
                  setData({ ...data, projectRemoteOption: e.target.value })
                }
                value={data.projectRemoteOption}
              />
              <input
                type="text"
                placeholder="Onsite %"
                className="ml-3"
                onChange={(e) =>
                  setData({ ...data, projectOnsiteOption: e.target.value })
                }
                value={data.projectOnsiteOption}
              />
            </>
          )}
        </div>
        <div
          className="flex-center mb-1"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 3fr",
            gridTemplateRows: "repeat (2,auto)",
          }}
        >
          <label
            for="fieldlabels"
            style={{ gridColumn: "1", gridRow: "1/2", alignSelf: "end" }}
          >
            Utilization Intensity
          </label>
          <input
            type="range"
            class="form-control ml-3"
            id="formControlRange"
            style={{
              gridColumn: "2",
              width: "47rem",
              margin: "10px 0",
              marginBottom: "0",
              padding: "0",
            }}
            min="0"
            max="100"
            step="1"
            list="steplist"
            value={data.utilizationIntensity}
            onChange={(e) => {
              setData({ ...data, utilizationIntensity: e.target.value });
            }}
          />
          <datalist id="steplist">
            <option>0</option>
            <option>10</option>
            <option>20</option>
            <option>30</option>
            <option>40</option>
            <option>50</option>
            <option>60</option>
            <option>70</option>
            <option>80</option>
            <option>90</option>
            <option>100</option>
          </datalist>
          <div
            class="ticks"
            style={{
              gridColumn: "2",
              gridRow: "2",
              display: "flex",
              justifyContent: "center",
              width: "41.5rem",
              marginLeft: "4rem",
            }}
          >
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              0
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              10
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              20
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              30
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              40
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              50
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              60
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              70
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              80
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              90
            </span>
            <span class="o_txt" style={{ margin: "0 1.7rem" }}>
              100
            </span>
          </div>
          <output>{data.utilizationIntensity} %</output>
        </div>
        <br />
        <div className="flex-center position-relative mb-1">
          <label className="fieldlabels">Total Days</label>
          <input
            type="text"
            placeholder="Total Days"
            onChange={(e) => {
              setData({ ...data, totalDays: e.target.value });
            }}
            value={data.totalDays}
          />
        </div>
        <br />
        <div className="flex-center position-relative mb-1">
          <>
            <div class="custom-control custom-control-right custom-switch">
              <input
                type="checkbox"
                class="custom-control-input"
                id="customSwitch-for-make-public"
                checked={data.public}
                onChange={(e) => {
                  setData({ ...data, public: e.target.checked });
                }}
              />
              {!data.public ? (
                <label
                  class="custom-control-label fieldlabels"
                  for="customSwitch-for-make-public"
                >
                  Publish
                </label>
              ) : (
                <label
                  class="custom-control-label fieldlabels"
                  for="customSwitch-for-make-public"
                >
                  Publish
                </label>
              )}
              {!data.public ? (
                <label
                  class="makePublicLabel"
                  style={{ marginLeft: "60px", fontWeight: "bold" }}
                >
                  NO!
                </label>
              ) : (
                <label
                  class="makePublicLabel"
                  style={{ marginLeft: "60px", fontWeight: "bold" }}
                >
                  YES!{" "}
                  <span style={{ fontWeight: "normal", marginLeft: "20px" }}>
                    (Please check if the project contains any confidential
                    information!)
                  </span>
                </label>
              )}
            </div>
          </>
        </div>
      </div>
      <input
        onClick={() => {
          console.log(data);
          if (
            startDateSelected &&
            deadlineSelected &&
            screeningQuestion.findIndex((q) => q.error === true) === -1
          ) {
            setActiveTab("finish");
          } else if (!startDateSelected && !deadlineSelected) {
            setStartDateClassName("border border-danger");
            setShowInvalidDate(true);
            setDeadlineClassName("border border-danger");
            setShowInvalidDeadlineDate(true);
          } else if (!startDateSelected) {
            setStartDateClassName("border border-danger");
            setShowInvalidDate(true);
          } else if (!deadlineSelected) {
            setDeadlineClassName("border border-danger");
            setShowInvalidDeadlineDate(true);
          } else if (
            screeningQuestion.findIndex((q) => q.error === true) !== -1
          ) {
            const hasError = screeningQuestion.findIndex(
              (q) => q.error === true
            );
            const dynamicInput = document.querySelector(
              `.questionInvalidTextSign${hasError}`
            );
            if (dynamicInput) {
              dynamicInput.style.display = "block";
            }
            const dynamicInputText = document.querySelector(
              `.question${hasError}`
            );
            if (dynamicInputText) {
              dynamicInputText.style.borderColor = "red";
            }
          }
        }}
        type="button"
        className="action-button"
        defaultValue="Next"
      />
      <input
        onClick={() => setActiveTab("projectDetails")}
        type="button"
        className="action-button-previous"
        defaultValue="Previous"
      />

      {showOptionsPopup && (
        <div className="prjdetails-conEdit-popup-wrapper">
          <div className="tab-pane fade show prjdetails-conEdit-div">
            <h4 style={{ marginBottom: "20px" }}>
              Add options for multiple choice
            </h4>
            <div className="row">
              {options.map((row, index) => (
                <div
                  className="flex-center position-relative mb-1"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <div
                    className={`optionRow${index}`}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <input
                      type="text"
                      className={`options${index}`}
                      placeholder="Enter option"
                      style={{ flex: "1", width: "600px" }}
                      value={row.value}
                      autoFocus={row.focus}
                      onChange={(e) =>
                        handleOptionsChange(index, e.target.value)
                      }
                    />
                    {index === 0 ? (
                      <i
                        className="fa-solid fa-circle-plus"
                        style={{
                          // margin: "5px",
                          marginBottom: "20px",
                          marginLeft: "10px",
                          fontSize: "20px",
                          color: "grey",
                          cursor: "pointer",
                        }}
                        onClick={handleAddOptions}
                      ></i>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div>
              <center>
                <button
                  type="button"
                  className="btn btn-light prjdetails-editCancel"
                  onClick={() => setShowOptionsPopup(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn base-btn"
                  style={{ marginLeft: "6px" }}
                  onClick={addOptions}
                >
                  Add
                </button>
              </center>
            </div>
          </div>
        </div>
      )}

      {showMatrixPopup && (
        <div className="prjdetails-conEdit-popup-wrapper">
          <div
            className="tab-pane fade show prjdetails-conEdit-div"
            style={{
              width: "1300px",
              height: "600px",
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            <h4 style={{ marginBottom: "20px", fontWeight: "bold" }}>
              Add Options & Choices
            </h4>
            <div
              id="container"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                id="leftContent"
                style={{
                  width: "50%",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <div>
                  <h5 style={{ fontWeight: "bold" }}>Options</h5>
                  <div className="row">
                    {questionsMatrix.map((q, index) => (
                      <div
                        className="flex-center position-relative mb-1"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div
                          className={`optionRow${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginLeft: "20px",
                          }}
                        >
                          <input
                            type="text"
                            className={`matrixQuestions${index}`}
                            placeholder={`Enter Option ${index + 1}`}
                            style={{ flex: "1", width: "500px" }}
                            value={q.question}
                            onChange={(e) =>
                              handleQuestionMatrixChange(index, e.target.value)
                            }
                          />
                          <i
                            className="fa-solid fa-minus-circle"
                            style={{
                              // margin: "5px",
                              marginBottom: "20px",
                              marginLeft: "10px",
                              fontSize: "20px",
                              color: "grey",
                              cursor: "pointer",
                            }}
                            onClick={(e) => handleRemoveQuestionMatrix(index)}
                          ></i>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-light prjdetails-editCancel"
                    onClick={handleAddQuestionMatrix}
                  >
                    Add Another Option
                  </button>
                </div>
                <br />
                <div>
                  <h5 style={{ fontWeight: "bold" }}>Choices</h5>
                  <div className="row">
                    {answersMatrix.map((a, index) => (
                      <div
                        className="flex-center position-relative mb-1"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div
                          className={`optionRow${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginLeft: "20px",
                          }}
                        >
                          <input
                            type="text"
                            className={`matrixAnswers${index}`}
                            placeholder={`Enter Choice ${index + 1}`}
                            style={{ flex: "1", width: "500px" }}
                            value={a.answer}
                            onChange={(e) =>
                              handleAnswerMatrixChange(index, e.target.value)
                            }
                          />
                          <i
                            className="fa-solid fa-minus-circle"
                            style={{
                              // margin: "5px",
                              marginBottom: "20px",
                              marginLeft: "10px",
                              fontSize: "20px",
                              color: "grey",
                              cursor: "pointer",
                            }}
                            onClick={(e) => handleRemoveAnswerMatrix(index)}
                          ></i>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-light prjdetails-editCancel"
                    onClick={handleAddAnswerMatrix}
                  >
                    Add Another Choice
                  </button>
                </div>
              </div>
              {/* Matrix Table with Styled First Row and First Column */}
              <div
                id="rightContent"
                style={{
                  width: "50%",
                  marginLeft: "10px",
                  marginRight: "10px",
                }}
              >
                <h5>Your Matrix will appear as follows:</h5>
                <table
                  id="matrix-table"
                  style={{
                    width: "100%",
                    textAlign: "center",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{ backgroundColor: "#f0f0f0", padding: "10px" }}
                      ></th>
                      {answersMatrix.map((a, index) => (
                        <th
                          key={index}
                          style={{
                            backgroundColor: "#f0f0f0",
                            padding: "10px",
                          }}
                        >
                          {a.answer || `Choice ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {questionsMatrix.map((q, qIndex) => (
                      <tr key={qIndex}>
                        <td
                          style={{
                            backgroundColor: "#f0f0f0",
                            padding: "10px",
                          }}
                        >
                          {q.question || `Option ${qIndex + 1}`}
                        </td>
                        {answersMatrix.map((_, aIndex) => (
                          <td key={aIndex} style={{ padding: "10px" }}>
                            <input
                              type="radio"
                              style={{
                                height: "15px",
                                width: "15px",
                                transform: "scale(1)", // Reset scaling
                                WebkitAppearance: "radio", // For Safari
                                appearance: "radio",
                              }}
                              name={`question-${qIndex}`}
                              value={`choice-${aIndex}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Display Questions and Answers in Matrix */}

            <br />
            <button
              type="button"
              className="btn btn-light prjdetails-editCancel"
              onClick={handleCloseModal}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn base-btn"
              onClick={handleFinalizeMatrix}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </fieldset>
  );
}
